function readPublic(key: "VITE_APP_NAME" | "VITE_PUBLIC_SITE_URL", fallback: string): string {
  const v = import.meta.env[key];
  return typeof v === "string" && v.trim().length > 0 ? v : fallback;
}

function readOptional(key: keyof ImportMetaEnv, fallback: string): string {
  const v = import.meta.env[key];
  return typeof v === "string" && v.trim().length > 0 ? v : fallback;
}

const publicSiteUrl = readPublic("VITE_PUBLIC_SITE_URL", "https://jmanatx.com");

/** Matches business card; override with VITE_CONTACT_PHONE_DISPLAY / VITE_CONTACT_PHONE_TEL if needed. */
const contactPhoneDisplay = readOptional("VITE_CONTACT_PHONE_DISPLAY", "512.633.3579");
const contactPhoneTel = readOptional("VITE_CONTACT_PHONE_TEL", "+15126333579");

function siteHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "jmanatx.com";
  }
}

export const config = {
  appName: readPublic("VITE_APP_NAME", "JMANATX"),
  /** Matches printed cards and primary domain branding. */
  brandLockup: "JMANATX" as const,
  publicSiteUrl,
  siteHostname: siteHostname(publicSiteUrl),
  contactPhoneDisplay,
  contactPhoneTel,
  /** Text this keyword to the number on the card to get a customer #. */
  smsSignupKeyword: "NEW",
  environment: import.meta.env.MODE,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const;

const ORDER_EMAIL = "jmanatxdelivers@proton.me";

/** Coarse mobile detection via UA + touch support. */
export function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || navigator.maxTouchPoints > 1;
}

/** Opens SMS with body pre-filled (mobile). */
export function smsSignupHref(): string {
  const body = encodeURIComponent(config.smsSignupKeyword);
  return `sms:${config.contactPhoneTel}?&body=${body}`;
}

/** Build an sms: URI with an arbitrary message body. */
export function smsHref(body: string): string {
  return `sms:${config.contactPhoneTel}?&body=${encodeURIComponent(body)}`;
}

/** Build a mailto: URI to the business email with subject + body. */
export function mailtoHref(subject: string, body: string): string {
  return (
    `mailto:${ORDER_EMAIL}` +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(body)}`
  );
}

/** Navigate to SMS on mobile or mailto on desktop. */
export function placeOrder(body: string, customerNumber: string): void {
  if (isMobileDevice()) {
    window.location.href = smsHref(body);
  } else {
    const subject = `JMANATX Order — Customer #${customerNumber}`;
    window.location.href = mailtoHref(subject, body);
  }
}

/**
 * Fire-and-forget email notification via serverless function.
 * Silently fails if the endpoint isn't configured (no backend yet).
 */
export function sendOrderEmail(body: string, customerNumber: string): void {
  const endpoint = "/api/order-email";
  fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customerNumber, orderText: body }),
  }).catch(() => {
    /* endpoint not deployed yet — silent fail */
  });
}
